import React from 'react';
import { useCalendarStore } from '@gik/calendar/store/CalendarStore';
import {
  CartItem,
  CheckoutForm,
  CheckoutFormPayload,
  CheckoutFormValues,
  OrderClaimStatus,
  PaymentConfirmationValues,
} from '@gik/checkout';
import bemBlock from '@gik/core/utils/bemBlock';
import { useInkindStore } from '@gik/inkind/store/InkindStore';
import { LoadingSpinner, UI } from '@gik/ui';
import noop from '@gik/core/utils/noop';
import moment from 'moment';
import { Analytics } from '@gik/core/utils';
import { placeOrder } from '@gik/checkout/api';
import { afterClaimSucceeded } from '../EventForm/utils';
import i18n from '@gik/i18n';
import { useUser } from '@gik/api/users/user';
import { useInkind } from '@gik/api/inkinds/inkind';
import { getRecipientContactInformation } from '@gik/calendar/utils/getRecipientContactInformation';
import { useUserStore } from '@gik/core/store/UserStore';
import { ICalendarEntry, ICalendarEvent } from '@gik/calendar/models/Calendar';
import { IWordpressCalendarEventType } from '@gik/core/models/wordpress/WordpressCalendarEventType';
import { StatusCodes } from 'http-status-codes';
import { isUserFollowingPage } from '@gik/inkind-page/utils/inkindPagePermissions';
import { useUserPages } from '@gik/api/inkinds/userPages';
import { createClaim } from '@gik/api/calendar/calendarClaims';
import { translationKeys as checkoutTranslationKeys } from '@gik/checkout/i18n/en';
import { GiftCardClaimConflictResolution } from '@gik/calendar';

export interface ICalendarClaimCheckoutFormProps extends React.HTMLAttributes<HTMLDivElement> {
  event: ICalendarEvent;
  entry: ICalendarEntry;
  serviceSlug: string;
  serviceCategorySlug: string;
  calendarEventTypes: IWordpressCalendarEventType[];
  setSelectedEvent?(event: ICalendarEvent);
  setSelectedEntry?(entry: ICalendarEntry);
  stepsNavPortal?: () => HTMLElement;
  buttonsPortal?: () => HTMLElement;

  onGotoStep?(): void;

  onFailed?(): void;
  onConflict?(): void;

  setOnDialogBack?(fn: () => void): void;
  onBack?(): void;
  onSuccess?(): void;
}

export default function CalendarClaimCheckoutForm({
  event,
  entry,
  className,
  serviceSlug,
  serviceCategorySlug,
  calendarEventTypes,
  onGotoStep,
  setOnDialogBack,
  stepsNavPortal,
  buttonsPortal,
  onFailed = noop,
  onConflict = noop,
  onSuccess = noop,
  onBack = noop,
  setSelectedEvent = noop,
  setSelectedEntry = noop,
}: ICalendarClaimCheckoutFormProps): React.ReactElement {
  const bem = bemBlock('calendar-claim-checkout-form');

  const userId = useUserStore(state => state.id);
  const { data: user } = useUser(userId);

  const inkindRouteId = useInkindStore(state => state.inkindRouteId);
  const { data: inkind } = useInkind(inkindRouteId);

  const setPaymentConfirmationValues = useCalendarStore(state => state.setPaymentConfirmationValues);
  const ignoreClaimConflict = useCalendarStore(state => state.ignoreClaimConflict);
  const setClaimFailErrorCode = useCalendarStore(state => state.setClaimFailErrorCode);
  const setGiftCardClaimConflictResolution = useCalendarStore(state => state.setGiftCardClaimConflictResolution);
  const setDialogTitle = useCalendarStore(state => state.setDialogTitle);
  const resolveGiftCardClaimConflictValues = useCalendarStore(state => state.resolveGiftCardClaimConflictValues);

  const product = useCalendarStore(state => state.selectedGiftcard);

  const { data: userPages, revalidate: revalidateUserPages } = useUserPages(userId);
  const isFollowing = isUserFollowingPage(inkind, userId, userPages);

  const eventType = calendarEventTypes.find(item => item.id === entry.typeId);

  if (!product) {
    throw new Error(`calendarStore does not have a selectedGiftcard`);
  }

  const [errorMessage, setErrorMessage] = React.useState<string>();
  const [initialData, setInitialData] = React.useState<CheckoutFormValues>();

  function getInitialCheckoutData() {
    // shipping
    const {
      email: recipientEmail,
      fullName: recipientFullName,
      address1: recipientAddress1,
      address2: recipientAddress2,
      cityAndState: recipientCityAndState,
      postcode: recipientPostcode,
    } = getRecipientContactInformation(inkind);

    const cart: CartItem[] = [{ productId: product.id, name: product.name, checkoutType: 'tango-card' }];
    if (product.variations && product.variations.length === 1) {
      cart[0].variationId = product.variations[0].id;
      if (product.variations[0].price > 0) {
        cart[0].price = product.variations[0].price;
      }
    }

    return {
      billing: {
        country: 'US',
        firstName: user.firstName,
        lastName: user.lastName,
        state: user.state,
        email: user.emailAddress,
      },
      shipping: {
        country: 'US',
        email: recipientEmail,
        firstName: recipientFullName,
        address1: recipientAddress1,
        address2: recipientAddress2,
        city: recipientCityAndState.split(',')[0].trim(),
        state: recipientCityAndState.split(',')[1].trim(),
        postalCode: recipientPostcode,
      },
      summary: { agree: false },
      cart: cart,
    } as CheckoutFormValues;
  }

  const getInitialCheckoutDataCB = React.useCallback(getInitialCheckoutData, [inkind, product, user]);

  function handleSuccess() {
    const claim = {
      serviceModule: '',
      serviceSlug,
      serviceCategorySlug,
    };
    afterClaimSucceeded(entry, claim, eventType);

    if (!isFollowing) {
      // revildate user pages to update the isFollowing state.
      revalidateUserPages();
    }

    if (setDialogTitle) {
      setDialogTitle('Confirmation');
    }
    onSuccess();
  }

  async function handleSubmit(values: CheckoutFormPayload, updateCheckoutStatusText: (text: string) => void) {
    // inject event-specific information (purchasing for an event)
    values.eventType = entry.typeName;
    values.eventDateTime = moment.utc(event.startsAt).toISOString();

    // generate encoded claim information to execute the claim
    values.createClaimRequest = {
      calendarEntryId: entry.id,
      claimDate: event.startsAt,
      serviceSlug,
    };

    if (!ignoreClaimConflict) {
      // check if the claim already exists for this event
      // if so, show a confirmation dialog
      const createClaimTestResponse = await createClaim({
        ...values.createClaimRequest,
        test: true,
      });

      if (createClaimTestResponse.status !== StatusCodes.NO_CONTENT) {
        if (createClaimTestResponse.status === StatusCodes.CONFLICT) {
          onConflict();
          setGiftCardClaimConflictResolution({
            checkoutFormPayload: values,
          });
          return;
        } else if (createClaimTestResponse.status === StatusCodes.GONE) {
          UI.error('Sorry, it looks like this request no longer exists.');
          setErrorMessage(i18n.t(checkoutTranslationKeys.genericBackendError));
          onFailed();
          return;
        } else {
          console.error(
            'Claim Test returned an unexpected response: ',
            createClaimTestResponse.status,
            createClaimTestResponse.statusText
          );
          setErrorMessage(i18n.t(checkoutTranslationKeys.genericBackendError));
          onFailed();
          return;
        }
      }
    }

    let response: PaymentConfirmationValues;
    try {
      response = await placeOrder(values, updateCheckoutStatusText);
    } catch (err) {
      console.error(err);
      setErrorMessage(err.message || i18n.t(checkoutTranslationKeys.genericBackendError));
      return;
    }

    // FIXME: remove duplication between here and checkout launcher
    Analytics.fireEvent('CheckoutCompletePurchase', {
      productNames: values.products?.map(cartItem => cartItem.name).join(','),
      productIds: values.products?.map(cartItem => cartItem.productId).join(','),
      productCheckoutTypes: values.products?.map(cartItem => cartItem.checkoutType).join(','),
      inkindRouteId,
      userId: user.id, // id of the purchaser
      initiatedOn: 'calendar',
      total: response.total.toString(),
      tip: response.tip.toString(),
      orderID: response.orderId.toString(),
    });

    if (response.claimStatus === OrderClaimStatus.Conflict) {
      setClaimFailErrorCode(StatusCodes.CONFLICT);
      onConflict();

      if (!ignoreClaimConflict) {
        setGiftCardClaimConflictResolution({
          checkoutFormPayload: values,
          paymentConfirmation: response,
        });
        return;
      }
    } else if (response.claimStatus === OrderClaimStatus.Gone) {
      setClaimFailErrorCode(StatusCodes.GONE);
      onFailed(); // refresh calendar
    } else if (response.claimStatus !== OrderClaimStatus.Success) {
      console.error('Unexpected claim status', response.claimStatus);
      return;
    }

    setPaymentConfirmationValues(response);
    handleSuccess();
  }

  React.useEffect(() => {
    if (user) {
      setInitialData(getInitialCheckoutDataCB());
    }
  }, [user, getInitialCheckoutDataCB]);

  React.useEffect(() => {
    setOnDialogBack(() => () => {
      onBack();
    });
  }, [setOnDialogBack]);

  // checkout form configuration
  const checkoutFormConfig = {
    shipping: false,
    hideProducts: false,
    skipProducts: false,
  };

  // wait until we have initial data
  if (!initialData) {
    return <LoadingSpinner center />;
  }

  return (
    <div className={bem(null, null, className)}>
      <CheckoutForm
        onSubmit={handleSubmit}
        onGotoStep={onGotoStep}
        initialData={initialData}
        {...checkoutFormConfig}
        errorMessage={errorMessage}
        stepsNavPortal={stepsNavPortal?.()}
        buttonsPortal={buttonsPortal?.()}
        initiatedOn="calendar"
        inkindRouteId={inkindRouteId}
        errorPage={
          resolveGiftCardClaimConflictValues && (
            <GiftCardClaimConflictResolution
              resolveGiftCardClaimConflictValues={resolveGiftCardClaimConflictValues}
              alreadyClaimedEntry={entry}
              alreadyClaimedEvent={event}
              setSelectedEvent={setSelectedEvent}
              setSelectedEntry={setSelectedEntry}
              buttonsPortal={buttonsPortal?.()}
              onAttachClaim={handleSuccess}
            />
          )
        }
      />
    </div>
  );
}
